import React, { useCallback, useMemo, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { TbTemplate } from 'react-icons/tb';
import useTemplateStore from '../../store/templates-store';
import useToolStore from '../../store/tools-store';
import useCrudListPage from '../../shared/hooks/useCrudListPage';
import DataTable from '../../shared/components/table/DataTable';
import PageHeader from '../../shared/components/table/PageHeader';
import SearchInput from '../../shared/components/table/SearchInput';
import RowActions from '../../shared/components/table/RowActions';
import ResourceFormModal from '../../shared/components/modal/ResourceFormModal';
import DeleteModal from '../../shared/components/modal/DeleteModal';
import TemplateExample from '../../shared/components/TemplateExample';
import { formatDate } from '../../shared/lib/formatters';
import { renderLargeCell } from '../../shared/lib/renderLargeCell';

const columnHelper = createColumnHelper();

const TemplatesPage = () => {
  const { templates, fetchTemplates, fetchTemplateById, createTemplate, updateTemplate, deleteTemplate } = useTemplateStore();
  const { tools, fetchTools } = useToolStore();

  const [expandedRows, setExpandedRows] = useState({});
  const [exampleModalOpen, setExampleModalOpen] = useState(false);
  const [selectedExampleTemplate, setSelectedExampleTemplate] = useState(null);
  const openEditRef = React.useRef(() => {});
  const openDeleteRef = React.useRef(() => {});

  const fetchAll = useCallback(async () => {
    await Promise.all([fetchTemplates(), fetchTools()]);
  }, [fetchTemplates, fetchTools]);

  const toolOptions = useMemo(
    () =>
      [...(tools || [])].sort((a, b) => a.name.localeCompare(b.name)).map((t) => ({ value: t.id, label: t.name })),
    [tools]
  );

  const fields = useMemo(
    () => [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'value', label: 'Value', type: 'textarea', required: true, rows: 8 },
      {
        name: 'tool_id',
        label: 'Tool',
        type: 'select',
        required: true,
        options: toolOptions,
        placeholder: 'Select Tool',
      },
    ],
    [toolOptions]
  );

  const items = useMemo(
    () =>
      (templates || []).map((template) => {
        const tool = tools.find((t) => t.id === template.tool_id);
        return { ...template, tool_name: tool?.name || ' ' };
      }),
    [templates, tools]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', { header: 'ID' }),
      columnHelper.accessor('name', { header: 'Name' }),
      columnHelper.accessor('value', {
        header: 'Value',
        cell: (info) => renderLargeCell(expandedRows, setExpandedRows, info, 'description'),
      }),
      columnHelper.accessor('tool_name', { header: 'Tool Name' }),
      columnHelper.accessor('created_at', {
        header: 'Created At',
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor('actions', {
        header: 'Actions',
        cell: ({ row }) => (
          <RowActions
            className="flex space-x-4 justify-center items-center"
            onEdit={() => openEditRef.current(row.original)}
            onDelete={() => openDeleteRef.current(row.original)}
            after={
              <TbTemplate
                title="Examples"
                onClick={() => {
                  setSelectedExampleTemplate(row.original);
                  setExampleModalOpen(true);
                }}
                size={18}
                className="cursor-pointer text-green-600"
              />
            }
          />
        ),
      }),
    ],
    [expandedRows]
  );

  const sortItems = useCallback(
    (data) => [...data].sort((a, b) => a.tool_name.localeCompare(b.tool_name) || a.name.localeCompare(b.name)),
    []
  );

  const {
    table,
    isLoading,
    globalFilter,
    setGlobalFilter,
    isFormOpen,
    formMode,
    isDeleteModalOpen,
    selectedItem,
    deletePayload,
    openCreate,
    openEdit,
    openDelete,
    closeModals,
    handleFormSubmit,
    handleDelete,
  } = useCrudListPage({
    items,
    fetchItems: fetchAll,
    createItem: createTemplate,
    updateItem: updateTemplate,
    deleteItem: deleteTemplate,
    columns,
    sortItems,
    entityLabel: 'Template',
    getDeletePayload: (item) => ({ ...item, dataName: 'template' }),
  });

  openEditRef.current = openEdit;
  openDeleteRef.current = openDelete;

  const initialValues = useMemo(
    () =>
      formMode === 'edit' && selectedItem
        ? { name: selectedItem.name || '', value: selectedItem.value || '', tool_id: selectedItem.tool_id || '' }
        : { name: '', value: '', tool_id: '' },
    [formMode, selectedItem]
  );

  const handleCloseExampleModal = () => {
    setExampleModalOpen(false);
    setSelectedExampleTemplate(null);
  };

  return (
    <div className="max-w-screen-3xl mx-auto mt-4">
      <PageHeader title="Templates List" onCreate={openCreate} />
      <div className="mb-4">
        <SearchInput value={globalFilter} onChange={setGlobalFilter} />
      </div>
      <DataTable table={table} columnsCount={columns.length} isLoading={isLoading} emptyMessage="No templates found" />
      <ResourceFormModal
        isOpen={isFormOpen}
        onClose={closeModals}
        mode={formMode}
        title={formMode === 'edit' ? 'Update Template' : 'Create Template'}
        fields={fields}
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
      />
      <DeleteModal isOpen={isDeleteModalOpen} onClose={closeModals} data={deletePayload} onDelete={handleDelete} />
      <TemplateExample
        isOpen={exampleModalOpen}
        onClose={handleCloseExampleModal}
        template={selectedExampleTemplate}
        setTemplate={setSelectedExampleTemplate}
        fetchById={fetchTemplateById}
        ownerType="system"
      />
    </div>
  );
};

export default TemplatesPage;
